import React, { useCallback, useEffect, useState } from 'react';
import * as Styles from './StudioPlansHandler.styles';
import withErrorBoundary from '@/utils/errorBoundary';
import BannerPlan from '../components/BannerPlan/BannerPlan';
import TablePlan from '../components/TablePlan/TablePlan';
import { studioActions } from '@/redux/modules';
import { useDispatch, useSelector } from 'react-redux';
import { formatCurrency } from '@/utils/Formatters/formatCurrency';
import { useStudio } from '@/hooks/studio';
import StudioPlansSkeleton from './StudioPlansSkeleton';
import ModalFeaturePro from '@/pages/Studio/components/ModalFeaturePro';
import { ID_PRO_FEATURES } from '@/pages/Studio/studio.enum';
import { useHistory } from 'react-router';
import useLocale from '@/hooks/useLocale';

const StudioPlansHandler = () => {
  const [displayModalFeaturePro, setDisplayModalFeaturePro] = useState(false);

  const { product, products: studioProducts } = useSelector(state => state.studio);

  const dispatch = useDispatch();
  const history = useHistory();
  const { routes: routesLocale } = useLocale();
  const { requestUserAccountData, provisioningTrailWithoutCreditCard } = useStudio();

  const productsData = studioProducts && studioProducts.data;
  const hasDiscount = productsData && productsData.has_discount;
  const planFreeData = productsData && productsData.studio_free;
  const planProData = productsData && productsData.studio_pro;
  const planTrialData = productsData && productsData.studio_trial;
  const planDiscountData = productsData && productsData.studio_discount;
  const trialAvailable = product && product.trial_available;
  const planFreePrice = planFreeData && planFreeData.price.split('.');
  const serviceId = product && product.hosting_id;

  const loading = !(studioProducts && studioProducts.data);

  const planFreeProps = {
    name: planFreeData && planFreeData.title,
    price: planFreeData && planFreePrice[0],
    currency: planFreeData && planFreeData.currency,
    total: planFreeData && planFreeData.total,
    planId: planFreeData && planFreeData.pid,
  };

  const planProProps = {
    name: planProData && planProData.title,
    price: hasDiscount ? planDiscountData && formatCurrency(planDiscountData.price) : planProData && formatCurrency(planProData.price),
    currency: planProData && planProData.currency,
    total: hasDiscount ? planDiscountData && formatCurrency(planDiscountData.total) : planProData && formatCurrency(planProData.total),
    planId: trialAvailable ? planDiscountData && planTrialData.pid : planProData && planProData.pid,
  };

  useEffect(() => {
    dispatch(studioActions.products.request());
  }, [dispatch]);

  useEffect(() => {
    requestUserAccountData();
  }, [requestUserAccountData]);

  const modalFeatureProProps = useCallback(() => {
    const productsData = studioProducts && studioProducts.data;

    const purchaseTrialWithoutCc = () => {
      const onSuccessCallback = () => {
        setDisplayModalFeaturePro(false);
        history.push(routesLocale.studio, { redirectFrom: routesLocale.fastcheckout });
      };

      provisioningTrailWithoutCreditCard(onSuccessCallback);
    };

    return {
      setModalFeatureProToShow: setDisplayModalFeaturePro,
      modalId: ID_PRO_FEATURES.STUDIO_PRO_OFFER,
      trialAvailable: productsData.trial_available,
      hasDiscount: productsData && productsData.has_discount,
      productTrial: productsData && productsData.studio_trial,
      productPro: productsData && productsData.studio_pro,
      discount: productsData && productsData.studio_discount,
      accountProduct: product,
      purchaseTrialWithoutCc,
    };
  }, [studioProducts, routesLocale, history, product, provisioningTrailWithoutCreditCard]);

  if (loading) {
    return (
      <StudioPlansSkeleton loading={loading} />
    );
  }

  return (
    <Styles.Container>
      <Styles.Content>
        <BannerPlan trialAvailable={trialAvailable} />
        <TablePlan
          trialAvailable={trialAvailable}
          planFree={planFreeProps}
          planPro={planProProps}
          planDiscountData={planDiscountData}
          hasDiscount={hasDiscount}
          serviceId={serviceId}
          setDisplayModalFeaturePro={setDisplayModalFeaturePro}
        />
      </Styles.Content>

      {displayModalFeaturePro && <ModalFeaturePro {...modalFeatureProProps()} />}
    </Styles.Container>
  );
};

export default withErrorBoundary(StudioPlansHandler, 'StudioPlansHandler');
